import React, { useRef, useState } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import { selectTheme } from "../../themeSlice";
import "./uploadCSV.css";

export default function CsvUploadModal({
  isOpen,
  onClose,
  organizationId,
  sessionId,
}) {
  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const splitCsvLine = (line) => {
    const out = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        out.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
    out.push(current);
    return out.map((cell) => cell.trim().replace(/^"|"$/g, ""));
  };

  const validateThreeOptionCsv = async (file) => {
    const text = await file.text();
    const [headerLine = ""] = text.split(/\r?\n/);
    const headers = splitCsvLine(headerLine).map((h) => h.toLowerCase());
    const hasOptionFour = headers.some((h) =>
      ["option_four", "option_4", "option4", "option d", "option_d"].includes(h)
    );
    if (hasOptionFour) {
      throw new Error("Option 4 is not allowed. Please upload CSV with only Option A, B, C.");
    }
  };

  const fileInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const adminToken = useSelector(selectAdminToken);
  const [isUploading, setIsUploading] = useState(false);
  const {
    availableThemes,
    currentTheme,
    status,
    data: themeData,
  } = useSelector((state) => state.theme);
  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      Swal.fire("Invalid File", "Please select a CSV file.", "error");
      e.target.value = null;
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
  };

  const handleUpload = async () => {
    if (!csvFile) {
      return Swal.fire("No File", "Please choose a CSV file first.", "warning");
    }

    setIsUploading(true); // start loading

    const formData = new FormData();
    try {
      await validateThreeOptionCsv(csvFile);
    } catch (validationError) {
      setIsUploading(false);
      return Swal.fire(
        "Invalid CSV",
        validationError?.message || "CSV validation failed.",
        "error"
      );
    }
    formData.append("csv", csvFile);
    formData.append("currentTheme", currentTheme);
    formData.append("double", themeData.double);
    formData.append("fifty_fifty", themeData.fifty_fifty);
    formData.append("lifelines", themeData.lifelines);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/uploadCSV`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
          body: formData,
        }
      );

      if (!res.ok) {
        const res_data = await res.json();
        console.log(res_data);

        // Stop uploading state immediately
        setIsUploading(false);

        if (res_data.type !== undefined) {
          console.log(res_data.message);

          // Clear file input so selecting the same filename again will trigger onChange
          setCsvFile(null);
          if (fileInputRef.current) fileInputRef.current.value = null;

          // Show server message + all row-level validation issues (if present)
          const errorList = Array.isArray(res_data.errors)
            ? res_data.errors.filter(Boolean)
            : [];
          if (errorList.length > 0) {
            const errorItemsHtml = errorList
              .map((item) => `<li>${escapeHtml(item)}</li>`)
              .join("");
            Swal.fire({
              title: "Failed to save CSV",
              icon: "info",
              html: `
                <div style="text-align:left;">
                  <div>${escapeHtml(res_data.message || "CSV validation failed")}</div>
                  <ul style="margin-top:8px;padding-left:18px;">${errorItemsHtml}</ul>
                </div>
              `,
            });
          } else {
            Swal.fire("Failed to save CSV", res_data.message, "info");
          }
        } else {
          throw new Error("CSV Upload failed");
        }
        return true;
      }

      Swal.fire("Success", "CSV imported successfully.", "success");
      window.location.reload();
      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to upload CSV. Please try again.", "error");
    } finally {
      setIsUploading(false); // stop loading after all
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="csv-modal">
        <h3>Upload Questions CSV</h3>
        <div className="csv-form-group">
          <button
            className="csv-file-btn"
            onClick={() => fileInputRef.current.click()}
          >
            Choose CSV File
          </button>
          <span className="csv-file-name">
            {csvFile?.name || "No file selected"}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="csv-file-input"
            onChange={handleFileChange}
          />
        </div>
        <div className="code">* Allowed File format: CSV Only</div>
        <div className="csv-actions">
          <button className="csv-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="csv-upload-btn"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </>
  );
}
